/**
 * Linguagem do contexto events no backoffice — espelha `events/domain`.
 * As regras aqui só avisam antes do clique; quem decide é a API.
 */
import { z } from 'zod';
import type { components } from '@/core/http';

export type AcademyEvent = components['schemas']['EventOut'];
export type EventBody = components['schemas']['EventIn'];
export type Registration = components['schemas']['RegistrationOut'];

export const EventStatus = { DRAFT: 'draft', PUBLISHED: 'published', CANCELLED: 'cancelled' } as const;

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  cancelled: 'Cancelado',
};

export const FORMATS = [
  { id: 'presencial', label: 'Presencial' },
  { id: 'online', label: 'Online' },
  { id: 'hibrido', label: 'Presencial e online' },
] as const;
export type EventFormat = (typeof FORMATS)[number]['id'];

export const ACCENTS = [
  { id: 'teal', label: 'Turquesa' },
  { id: 'lime', label: 'Verde-limão' },
  { id: 'violet', label: 'Violeta' },
] as const;

/**
 * Limites idênticos aos de `events/presentation/schemas.py` (EventIn,
 * ArtworkIn) e do domínio: o que o formulário aceita, a API aceita.
 */
export const LIMITS = {
  title: { min: 3, max: 200 },
  category: 80,
  organizer: 120,
  description: 20_000,
  venue: 200,
  address: 300,
  city: 120,
  onlineUrl: 500,
  capacity: { min: 1, max: 2_147_483_647 },
  kicker: 80,
  series: 120,
  headline: 200,
  tagline: 300,
} as const;

const URL_PATTERN = /^https?:\/\/[^\s/$.?#][^\s]*$/i;

const max = (n: number) => `No máximo ${n.toLocaleString('pt-BR')} caracteres`;
const text = (n: number) => z.string().trim().max(n, max(n));

/** "−19,47" e "-19.47" são o mesmo número; vazio é "não informado". */
export function parseDecimal(v: string): number | null {
  const t = v.trim().replace(',', '.');
  if (t === '') return null;
  return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : Number.NaN;
}

const coordinate = (limit: number, name: string) =>
  z
    .string()
    .refine((v) => !Number.isNaN(parseDecimal(v)), 'Use um número, ex.: -19,4706')
    .refine((v) => {
      const n = parseDecimal(v);
      // Não-número já tem a mensagem dele; aqui só o intervalo.
      return n === null || Number.isNaN(n) || Math.abs(n) <= limit;
    }, `${name} vai de -${limit} a ${limit}`);

export const eventFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(LIMITS.title.min, `Mínimo de ${LIMITS.title.min} caracteres`)
      .max(LIMITS.title.max, max(LIMITS.title.max)),
    /** Gerado pela API a partir do título; o formulário só o devolve intacto. */
    slug: z.string(),
    category: text(LIMITS.category),
    organizer: text(LIMITS.organizer),
    description: z.string().max(LIMITS.description, max(LIMITS.description)),
    startsAt: z.string().min(1, 'Informe a data e a hora'),
    endsAt: z.string(),
    format: z.enum(['presencial', 'online', 'hibrido']),
    venue: text(LIMITS.venue),
    address: text(LIMITS.address),
    city: text(LIMITS.city),
    latitude: coordinate(90, 'Latitude'),
    longitude: coordinate(180, 'Longitude'),
    onlineUrl: text(LIMITS.onlineUrl).refine(
      (v) => v === '' || URL_PATTERN.test(v),
      'Use um endereço completo, começando com https://',
    ),
    capacity: z
      .string()
      .trim()
      .refine((v) => v === '' || /^\d+$/.test(v), 'Só números inteiros')
      .refine(
        (v) => v === '' || (Number(v) >= LIMITS.capacity.min && Number(v) <= LIMITS.capacity.max),
        'Informe ao menos 1 vaga (ou deixe em branco para não limitar)',
      ),
    featured: z.boolean(),
    imageKey: z.string().nullable(),
    imageUrl: z.string().nullable(),
    kicker: text(LIMITS.kicker),
    series: text(LIMITS.series),
    headline: text(LIMITS.headline),
    tagline: text(LIMITS.tagline),
    accent: z.enum(['teal', 'lime', 'violet']),
  })
  .refine((f) => !f.endsAt || f.endsAt > f.startsAt, {
    path: ['endsAt'],
    message: 'O término precisa ser depois do início',
  })
  // Espelha `Place`: meio par de coordenadas não marca ponto nenhum no mapa.
  .refine((f) => (f.latitude.trim() === '') === (f.longitude.trim() === ''), {
    path: ['longitude'],
    message: 'Informe latitude e longitude juntas (ou nenhuma)',
  });
export type EventForm = z.infer<typeof eventFormSchema>;

/** ISO → valor de `<input type="datetime-local">` no fuso do navegador. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` → ISO com fuso (a API recusa data sem fuso). */
export function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

export function emptyForm(): EventForm {
  return {
    title: '',
    slug: '',
    category: 'Conteúdo Educacional',
    organizer: '',
    description: '',
    startsAt: '',
    endsAt: '',
    format: 'presencial',
    venue: '',
    address: '',
    city: '',
    latitude: '',
    longitude: '',
    onlineUrl: '',
    capacity: '',
    featured: false,
    imageKey: null,
    imageUrl: null,
    kicker: '',
    series: '',
    headline: '',
    tagline: '',
    accent: 'teal',
  };
}

export function toForm(e: AcademyEvent): EventForm {
  return {
    title: e.title,
    slug: e.slug,
    category: e.category,
    organizer: e.organizer,
    description: e.description,
    startsAt: toLocalInput(e.starts_at),
    endsAt: toLocalInput(e.ends_at),
    format: e.format as EventFormat,
    venue: e.venue,
    address: e.address,
    city: e.city,
    latitude: e.latitude?.toString() ?? '',
    longitude: e.longitude?.toString() ?? '',
    onlineUrl: e.online_url ?? '',
    capacity: e.capacity?.toString() ?? '',
    featured: e.featured,
    imageKey: e.image_key,
    imageUrl: e.image_url,
    kicker: e.artwork.kicker,
    series: e.artwork.series,
    headline: e.artwork.headline,
    tagline: e.artwork.tagline,
    accent: (['teal', 'lime', 'violet'].includes(e.artwork.accent)
      ? e.artwork.accent
      : 'teal') as EventForm['accent'],
  };
}

const num = (v: string) => parseDecimal(v);

export function toBody(f: EventForm): EventBody {
  return {
    title: f.title.trim(),
    slug: f.slug.trim() || null,
    category: f.category.trim(),
    organizer: f.organizer.trim(),
    description: f.description,
    starts_at: fromLocalInput(f.startsAt)!,
    ends_at: fromLocalInput(f.endsAt),
    format: f.format,
    venue: f.venue.trim(),
    address: f.address.trim(),
    city: f.city.trim(),
    latitude: num(f.latitude),
    longitude: num(f.longitude),
    online_url: f.onlineUrl.trim() || null,
    capacity: num(f.capacity),
    featured: f.featured,
    image_key: f.imageKey,
    artwork: {
      kicker: f.kicker,
      series: f.series,
      headline: f.headline,
      tagline: f.tagline,
      accent: f.accent,
    },
  };
}

const dateFmt = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export const formatWhen = (iso: string) => dateFmt.format(new Date(iso));

/** Selo de data: { month: "OUT", day: "14" }. */
export function dateBadge(iso: string): { month: string; day: string } {
  const d = new Date(iso);
  return {
    month: new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(d).replace('.', '').toUpperCase(),
    day: String(d.getDate()).padStart(2, '0'),
  };
}

export function isPast(e: Pick<AcademyEvent, 'starts_at'>, now = new Date()): boolean {
  return new Date(e.starts_at).getTime() < now.getTime();
}

/** "12 inscritos de 80", "3 inscritos". */
export function seatsLabel(e: Pick<AcademyEvent, 'registrations' | 'capacity'>): string {
  const n = e.registrations === 1 ? '1 inscrito' : `${e.registrations} inscritos`;
  return e.capacity ? `${n} de ${e.capacity}` : n;
}

// ───────────── formatação dos campos (aplicada ao digitar ou ao sair) ─────────────

/** Espaços repetidos viram um, e some o espaço das pontas. */
export const collapseSpaces = (v: string) => v.replace(/\s+/g, ' ').trim();

/** Vagas: só dígitos, sem zero à esquerda. */
export const digitsOnly = (v: string) => v.replace(/\D/g, '').replace(/^0+(?=\d)/, '');

/** Coordenada enquanto se digita: sinal, dígitos e um separador decimal. */
export function decimalInput(v: string): string {
  const cleaned = v.replace(/[^\d,.-]/g, '').replace(/(?!^)-/g, '');
  const [int, ...rest] = cleaned.split(/[,.]/);
  return rest.length ? `${int},${rest.join('')}` : int!;
}

/** "zoom.us/j/123" → "https://zoom.us/j/123". Vazio continua vazio. */
export function normalizeUrl(v: string): string {
  const t = v.trim();
  // Já tem esquema (inclusive um errado, como ftp://): deixa a validação apontar.
  if (!t || /^[a-z][a-z0-9+.-]*:\/\//i.test(t)) return t;
  return `https://${t.replace(/^\/+/, '')}`;
}

// ───────────── publicação ─────────────

/**
 * Por que o evento ainda não pode ser publicado — espelha
 * `Event._check_publishable` (o rascunho pode ser salvo incompleto).
 */
export function publishBlockers(e: Pick<AcademyEvent, 'format' | 'venue' | 'address'>): string[] {
  if (e.format !== 'online' && !(e.venue.trim() && e.address.trim()))
    return ['Evento presencial ou híbrido precisa de local e endereço.'];
  return [];
}

// ───────────── inscritos ─────────────

const csvCell = (v: unknown) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Lista de inscritos para o Excel: BOM UTF-8 e separador `;`. */
export function registrationsCsv(rows: readonly Registration[]): string {
  const head = ['Nome', 'E-mail', 'Cargo', 'Área', 'Inscrito em'];
  const body = rows.map((r) => [r.full_name, r.email, r.cargo, r.area, r.registered_at]);
  return '﻿' + [head, ...body].map((r) => r.map(csvCell).join(';')).join('\r\n');
}
